import React, { useEffect, useState } from "react";
import { FaSlidersH } from "react-icons/fa";
import "../assets/css/ResultsMobile.css";

/** Compact viewport matches Cloud Office filter collapse (≤1199.98px). */
export function usePortalCompactFilters() {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isCompactViewport, setIsCompactViewport] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia("(max-width: 1199.98px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 1199.98px)");
    const update = () => setIsCompactViewport(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  return {
    filtersOpen,
    setFiltersOpen,
    isCompactViewport,
    panelClassName: `cportal-filters co-filters-collapsible${
      filtersOpen || !isCompactViewport ? " is-open" : ""
    }`,
  };
}

/**
 * Search + collapsible filters (Cloud Office pattern).
 * Compact: search input with filter icon; filters collapsed by default.
 * Desktop: Show Filters toggle; search lives in the filter body.
 * Clear + Add CTA share one row after the filters.
 */
export default function PortalFiltersCollapsible({
  search,
  onSearchChange,
  searchPlaceholder = "Search…",
  searchAriaLabel = "Search",
  filtersOpen,
  setFiltersOpen,
  isCompactViewport,
  hasActiveFilters = false,
  onClear,
  actions = null,
  children,
}) {
  const open = Boolean(filtersOpen);
  const compact = Boolean(isCompactViewport);
  const canClear = typeof onClear === "function" && (!compact || open);
  const showActionRow = canClear || Boolean(actions);

  return (
    <div
      className={`cportal-filters co-filters-collapsible${
        open || !compact ? " is-open" : ""
      }`}
    >
      {compact ? (
        <div className="co-filter-search-bar">
          <input
            type="text"
            className="co-filter-search-bar__input"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label={searchAriaLabel}
          />
          <button
            type="button"
            className={`co-filter-search-bar__icon${open ? " is-active" : ""}`}
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? "Hide Filters" : "Show Filters"}
            title={open ? "Hide Filters" : "Show Filters"}
          >
            <FaSlidersH aria-hidden />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="co-filters-toggle"
          onClick={() => setFiltersOpen((o) => !o)}
          aria-expanded={open}
          aria-label={open ? "Hide Filters" : "Show Filters"}
          title={open ? "Hide Filters" : "Show Filters"}
        >
          <FaSlidersH aria-hidden />
          <span className="co-filters-toggle__label">
            {open ? "Hide Filters" : "Show Filters"}
          </span>
        </button>
      )}

      <div className="co-filters-body">
        {!compact ? (
          <div className="cportal-filter-row cportal-filter-row--desktop-search">
            <input
              className="cportal-search cportal-filter-search"
              type="text"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              aria-label={searchAriaLabel}
            />
          </div>
        ) : null}

        {children}
      </div>

      {showActionRow ? (
        <div
          className={`cportal-filter-actions co-filter-actions${
            canClear && actions ? " cportal-filter-actions--pair" : ""
          }`}
        >
          {compact ? actions : null}
          {canClear ? (
            <button
              type="button"
              className="cportal-clear-filters"
              onClick={onClear}
              disabled={!hasActiveFilters}
            >
              Clear
            </button>
          ) : null}
          {compact ? null : actions}
        </div>
      ) : null}
    </div>
  );
}
